import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import iconFileDownload from "../../../assets/media/icon-file-download.svg";
import { Link } from "react-router-dom";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { canMutateRenewal } from "../../../utility/rbac";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import {
  fetchApplications,
  fetchV2Applications,
  clearApplications,
} from "../../../store/reducers/applicationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import iconSearch from "../../../assets/media/icon-search.svg";
import { formatDate } from "../dashboard/aapDashboard";
import {
  buildLifecycleDateParams,
  filterItemsByDateRange,
} from "../../../utility/lifecycleDates";

const columnHelper = createColumnHelper();

const AppRenewals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const pagination = useSelector((state) => state.applications.pagination);
  const applicationLoading = useSelector((state) => state.applications.fetchLoading);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Clear applications when component mounts to ensure fresh data
  useEffect(() => {
    dispatch(clearApplications());
  }, [dispatch]);

  // Fetch applications when date filter changes
  useEffect(() => {
    const fetchData = async () => {
      const queryParams = {
        accessToken: authToken,
        state: "approved",
        dueInDays: 15,
        page: 1, // Always fetch all data from page 1
        limit: 1000, // Fetch a large number to ensure we get all records for client-side filtering
        sortOrder: "DESC",
        sortField: "id",
      };

      if (searchQuery && searchQuery.trim() !== '') {
        queryParams[searchBy] = searchQuery.trim();
      }

      // Due Date is the table column — filter by due date, not created date
      Object.assign(
        queryParams,
        buildLifecycleDateParams("due", startDate, endDate)
      );

      try {
        const response = await fetchV2Applications(queryParams);
        const apps = response?.applications || [];

        // Filter based on company if not admin
        let filteredData = apps.filter(app => {
          // Check if admin or matches company ID
          // The API returns companyId, user object has company_id
          const userCompanyId = user?.user?.company_id;
          const appCompanyId = app.companyId || app.company_id;

          if (user?.user?.is_admin) return true;
          return userCompanyId === appCompanyId;
        });

        // Ensure range matches the Due Date column even if API ignores dueFrom/dueTo
        filteredData = filterItemsByDateRange(
          filteredData,
          "due",
          startDate,
          endDate
        );

        setFilteredApplication(filteredData);
      } catch (error) {
        console.error("Failed to fetch renewals", error);
        setFilteredApplication([]);
      }
    };

    fetchData();
  }, [authToken, startDate, endDate, currentPage, searchQuery, itemsPerPage, searchBy]);



  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "applicationId") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const getTotalPages = () => {
    const validItemsPerPage = itemsPerPage || 10;
    return Math.ceil(filteredApplication.length / validItemsPerPage) || 1;
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;
    const id = applicationId || item.id;

    // Prevent multiple downloads - check if already downloading
    if (downloadingIds.includes(id)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, id]);

    try {
      await handleFilesDownload({ userId, applicationId: id, authToken });
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((downloadId) => downloadId !== id));
    }
  }, [authToken, downloadingIds]);

  // Client-side pagination slice
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice(startIndex, endIndex);
  }, [filteredApplication, currentPage, itemsPerPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("id", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name?.length > 50 ? `${row.name.substring(0, 50)}...` : row.name}</strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          // Find company admin user for this application's company
          const companyId = row.company_id || row.companyId;
          const companyAdmin = getallUserdata?.find(
            (user) => user.company_id === companyId && user.user_role === "company_admin"
          );
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.company_email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("due_date", {
        header: "Due Date",
        cell: (info) => <span className="dateText">{formatDate(info.getValue()) || ""}</span>,
      }),
      columnHelper.accessor("issue_date", {
        header: "Last Registered",
        cell: (info) => <span className="dateText">{formatDate(info.getValue()) || ""}</span>,
      }),
      columnHelper.display({
        id: "view",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    {downloadingIds.includes(row.applicationId || row.id) ? (
                      <span className="dropdown-item disabled" style={{ cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' }}>
                        Downloading...
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = row.applicationId || row.id;
                          if (!downloadingIds.includes(id)) {
                            handleDownload(row);
                          }
                        }}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent', width: '100%', textAlign: 'left', padding: '0 16px' }}
                      >
                        Download
                      </button>
                    )}
                  </li>
                  <li>
                    <Link
                      onClick={() => navigate(`/view-applications/${row.applicationId}`)}
                      className="dropdown-item"
                    >
                      View
                    </Link>
                  </li>
                  {canMutateRenewal(user) && (
                    <li>
                      <Link
                        onClick={() => navigate(`/view-applications/${row.applicationId}`)}
                        className="dropdown-item"
                      >
                        Send Email
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, navigate, getallUserdata, handleDownload, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Renewals</h2>
            </div>
            <div className="boardHeadRight">
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="applicationId">Search by Application ID</option>
                  <option value="companyName">Search by Company Name</option>
                  <option value="username">Search by Employee Name</option>
                  {/* <option value="email">Search by Email </option> */}
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={searchBy === "applicationId" ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={searchBy === "applicationId" && searchQuery ? `GB-${searchQuery}` : searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive" style={{ position: 'relative' }}>
            {/* Loader overlay for table section */}
            {applicationLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '4px'
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading applications...</p>
                </div>
              </div>
            )}
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {applicationLoading ? (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      {applicationLoading ? "Loading..." : "Result not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AppRenewals;